/**
 * Theme Registry for Machina
 * Contains all available themes and utilities for theme management
 */

import { MachinaTheme, defaultTheme } from './types';

/**
 * Melnibonéan Theme (Default)
 * Dark and decadent, inspired by Robert Gould's Elric illustrations
 * Deep blacks, warm gold accents, silver highlights
 */
export const melniboneTheme = defaultTheme;

/**
 * Crimson Keep Theme
 * Dark fortress aesthetic - blood red and black
 * Aggressive, martial, danger-focused
 */
export const crimsonKeepTheme: MachinaTheme = {
  id: 'crimson-keep',
  name: 'Crimson Keep',
  description: 'Dark fortress aesthetic with blood red accents',

  colors: {
    // Blood red primary
    primary: '#8b0000',
    primaryDark: '#5c0000',
    primaryLight: '#b22222',

    // Steel secondary
    secondary: '#708090',
    accent: '#dc143c',

    // Semantic
    success: '#228b22',
    warning: '#ff8c00',
    error: '#ff0000',
    info: '#4682b4',

    // Deep blacks
    background: '#0a0808',
    backgroundElevated: '#120e0e',
    surface: '#1a1414',
    surfaceElevated: '#241c1c',

    // Text - pale bone
    text: '#f5f0eb',
    textSecondary: '#c4b8ab',
    textMuted: '#6b5c52',
    textInverse: '#0a0808',

    // Borders - dark iron
    border: '#2a2020',
    borderLight: '#1e1616',
    borderFocus: '#dc143c',

    // Game-specific
    dmOnly: '#5c1a1a',
    dmChat: '#b22222',
    playerOnly: '#3d2820',
    aiSuggestion: '#6b3a2a',

    // Grid/map
    gridLine: '#1e1414',
    fogOfWar: '#060404',
    tokenHighlight: '#dc143c',

    // Dice colors
    diceD4: '#dc143c',
    diceD6: '#708090',
    diceD8: '#4682b4',
    diceD10: '#ff8c00',
    diceD12: '#8b0000',
    diceD20: '#ffd700',

    // Extended palette
    purple: '#800080',
    cyan: '#008b8b',
    orange: '#ff4500',
    pink: '#c71585',
  },

  typography: {
    fontDisplay: '"Cinzel", Georgia, "Times New Roman", serif',
    fontBody: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontMono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontGlyph: 'Georgia, "Times New Roman", serif',

    fontSizeXs: '0.75rem',
    fontSizeSm: '0.875rem',
    fontSizeMd: '1rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '2rem',

    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontWeightBlack: 900,

    lineHeightTight: 1.2,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.75,
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },

  borders: {
    radiusNone: '0',
    radiusSm: '2px',
    radiusMd: '4px',
    radiusLg: '8px',
    radiusFull: '9999px',

    widthThin: '1px',
    widthNormal: '2px',
    widthThick: '3px',
  },

  shadows: {
    sm: '0 1px 3px rgba(139, 0, 0, 0.3)',
    md: '0 4px 12px rgba(0, 0, 0, 0.5)',
    lg: '0 10px 24px rgba(0, 0, 0, 0.6)',
    xl: '0 20px 40px rgba(0, 0, 0, 0.7)',
  },

  effects: {
    transitionFast: '150ms ease-out',
    transitionNormal: '250ms ease-out',
    transitionSlow: '400ms ease-out',
  },

  decorations: {
    cornerFlourishes: true,
    decorativeBorders: true,
    sectionDividers: true,
    motif: 'geometric',
    flourishOpacity: 0.4,
    borderPattern: 'solid',
    headerStyle: 'underlined',
    panelStyle: 'beveled',
  },
};

/**
 * Fey Court Theme
 * Enchanted forest aesthetic - emerald and gold
 * Whimsical, natural, fey-touched
 */
export const feyCourtTheme: MachinaTheme = {
  id: 'fey-court',
  name: 'Fey Court',
  description: 'Enchanted forest aesthetic with emerald and gold',

  colors: {
    // Emerald primary
    primary: '#2e8b57',
    primaryDark: '#1e5c3a',
    primaryLight: '#3cb371',

    // Gold secondary
    secondary: '#daa520',
    accent: '#ffd700',

    // Semantic
    success: '#32cd32',
    warning: '#ffa500',
    error: '#dc143c',
    info: '#20b2aa',

    // Deep forest
    background: '#0a0f0a',
    backgroundElevated: '#101810',
    surface: '#142014',
    surfaceElevated: '#1c2c1c',

    // Text - moonlit silver
    text: '#e8f0e8',
    textSecondary: '#a8c8a8',
    textMuted: '#5a7a5a',
    textInverse: '#0a0f0a',

    // Borders - vine
    border: '#1e3020',
    borderLight: '#162418',
    borderFocus: '#ffd700',

    // Game-specific
    dmOnly: '#2a4a2a',
    dmChat: '#daa520',
    playerOnly: '#203820',
    aiSuggestion: '#3a5a3a',

    // Grid/map
    gridLine: '#1a2a1a',
    fogOfWar: '#040804',
    tokenHighlight: '#ffd700',

    // Dice colors
    diceD4: '#32cd32',
    diceD6: '#daa520',
    diceD8: '#20b2aa',
    diceD10: '#ffd700',
    diceD12: '#8b4513',
    diceD20: '#ff69b4',

    // Extended palette
    purple: '#9370db',
    cyan: '#00ced1',
    orange: '#ff8c00',
    pink: '#ff69b4',
  },

  typography: {
    fontDisplay: '"Cinzel", Georgia, "Times New Roman", serif',
    fontBody: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontMono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontGlyph: 'Georgia, "Times New Roman", serif',

    fontSizeXs: '0.75rem',
    fontSizeSm: '0.875rem',
    fontSizeMd: '1rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '2rem',

    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    fontWeightBlack: 700,

    lineHeightTight: 1.2,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.75,
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },

  borders: {
    radiusNone: '0',
    radiusSm: '4px',
    radiusMd: '8px',
    radiusLg: '12px',
    radiusFull: '9999px',

    widthThin: '1px',
    widthNormal: '1px',
    widthThick: '2px',
  },

  shadows: {
    sm: '0 1px 3px rgba(0, 100, 0, 0.2)',
    md: '0 4px 12px rgba(0, 0, 0, 0.4)',
    lg: '0 10px 24px rgba(0, 0, 0, 0.5)',
    xl: '0 20px 40px rgba(0, 0, 0, 0.6)',
  },

  effects: {
    transitionFast: '150ms ease-out',
    transitionNormal: '300ms ease-out',
    transitionSlow: '500ms ease-out',
  },

  decorations: {
    cornerFlourishes: true,
    decorativeBorders: true,
    sectionDividers: true,
    motif: 'vine',
    flourishOpacity: 0.3,
    borderPattern: 'ornate',
    headerStyle: 'ornate',
    panelStyle: 'inset',
  },
};

/**
 * Astral Sea Theme
 * Cosmic void aesthetic - deep blues and purples
 * Mystical, otherworldly, planar
 */
export const astralSeaTheme: MachinaTheme = {
  id: 'astral-sea',
  name: 'Astral Sea',
  description: 'Cosmic void aesthetic with deep blues and purple stars',

  colors: {
    // Deep indigo primary
    primary: '#4b0082',
    primaryDark: '#2e0052',
    primaryLight: '#6a0dad',

    // Starlight secondary
    secondary: '#e6e6fa',
    accent: '#00bfff',

    // Semantic
    success: '#7cfc00',
    warning: '#ffa07a',
    error: '#ff1493',
    info: '#00bfff',

    // Cosmic void
    background: '#05050a',
    backgroundElevated: '#0a0a14',
    surface: '#10101e',
    surfaceElevated: '#181828',

    // Text - starlight
    text: '#e6e6fa',
    textSecondary: '#b8b8dc',
    textMuted: '#6060a0',
    textInverse: '#05050a',

    // Borders - nebula
    border: '#202040',
    borderLight: '#141428',
    borderFocus: '#00bfff',

    // Game-specific
    dmOnly: '#2a2050',
    dmChat: '#9370db',
    playerOnly: '#1a1840',
    aiSuggestion: '#3a3070',

    // Grid/map
    gridLine: '#18182e',
    fogOfWar: '#020204',
    tokenHighlight: '#00bfff',

    // Dice colors
    diceD4: '#ff1493',
    diceD6: '#e6e6fa',
    diceD8: '#00bfff',
    diceD10: '#ffa07a',
    diceD12: '#4b0082',
    diceD20: '#ffd700',

    // Extended palette
    purple: '#9370db',
    cyan: '#00ced1',
    orange: '#ff7f50',
    pink: '#ff69b4',
  },

  typography: {
    fontDisplay: '"Cinzel", Georgia, "Times New Roman", serif',
    fontBody: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontMono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontGlyph: 'Georgia, "Times New Roman", serif',

    fontSizeXs: '0.75rem',
    fontSizeSm: '0.875rem',
    fontSizeMd: '1rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '2rem',

    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    fontWeightBlack: 700,

    lineHeightTight: 1.2,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.75,
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },

  borders: {
    radiusNone: '0',
    radiusSm: '4px',
    radiusMd: '6px',
    radiusLg: '10px',
    radiusFull: '9999px',

    widthThin: '1px',
    widthNormal: '1px',
    widthThick: '2px',
  },

  shadows: {
    sm: '0 1px 3px rgba(75, 0, 130, 0.3)',
    md: '0 4px 12px rgba(0, 0, 0, 0.5)',
    lg: '0 10px 24px rgba(0, 0, 20, 0.6)',
    xl: '0 20px 40px rgba(0, 0, 20, 0.7)',
  },

  effects: {
    transitionFast: '150ms ease-out',
    transitionNormal: '250ms ease-out',
    transitionSlow: '400ms ease-out',
  },

  decorations: {
    cornerFlourishes: true,
    decorativeBorders: true,
    sectionDividers: true,
    motif: 'geometric',
    flourishOpacity: 0.25,
    borderPattern: 'dashed',
    headerStyle: 'ornate',
    panelStyle: 'glowing',
  },
};

/**
 * Parchment Theme
 * Classic D&D aesthetic - warm parchment and brown
 * Traditional, nostalgic, readable
 */
export const parchmentTheme: MachinaTheme = {
  id: 'parchment',
  name: 'Parchment',
  description: 'Classic D&D aesthetic with warm parchment tones',

  colors: {
    // Sepia brown primary
    primary: '#8b4513',
    primaryDark: '#5c2e0e',
    primaryLight: '#a0522d',

    // Deep red secondary
    secondary: '#800020',
    accent: '#b8860b',

    // Semantic
    success: '#228b22',
    warning: '#ff8c00',
    error: '#8b0000',
    info: '#4682b4',

    // Warm parchment (light theme!)
    background: '#f5e6c8',
    backgroundElevated: '#efe0bc',
    surface: '#e8d4a8',
    surfaceElevated: '#dcc898',

    // Text - dark brown ink
    text: '#2a1a0a',
    textSecondary: '#4a3a2a',
    textMuted: '#7a6a5a',
    textInverse: '#f5e6c8',

    // Borders - brown
    border: '#c8b088',
    borderLight: '#d8c098',
    borderFocus: '#8b4513',

    // Game-specific
    dmOnly: '#d4c098',
    dmChat: '#800020',
    playerOnly: '#e0d0a8',
    aiSuggestion: '#c8b888',

    // Grid/map
    gridLine: '#c8b898',
    fogOfWar: '#a89878',
    tokenHighlight: '#8b4513',

    // Dice colors
    diceD4: '#8b0000',
    diceD6: '#f5f5dc',
    diceD8: '#4682b4',
    diceD10: '#b8860b',
    diceD12: '#800020',
    diceD20: '#ffd700',

    // Extended palette
    purple: '#663399',
    cyan: '#008b8b',
    orange: '#d2691e',
    pink: '#c71585',
  },

  typography: {
    fontDisplay: '"Cinzel", Georgia, "Times New Roman", serif',
    fontBody: 'Georgia, "Times New Roman", serif',
    fontMono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontGlyph: 'Georgia, "Times New Roman", serif',

    fontSizeXs: '0.75rem',
    fontSizeSm: '0.875rem',
    fontSizeMd: '1rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '2rem',

    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontWeightBlack: 900,

    lineHeightTight: 1.25,
    lineHeightNormal: 1.6,
    lineHeightRelaxed: 1.8,
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },

  borders: {
    radiusNone: '0',
    radiusSm: '2px',
    radiusMd: '4px',
    radiusLg: '6px',
    radiusFull: '9999px',

    widthThin: '1px',
    widthNormal: '2px',
    widthThick: '3px',
  },

  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 12px rgba(0, 0, 0, 0.15)',
    lg: '0 10px 24px rgba(0, 0, 0, 0.2)',
    xl: '0 20px 40px rgba(0, 0, 0, 0.25)',
  },

  effects: {
    transitionFast: '150ms ease-out',
    transitionNormal: '250ms ease-out',
    transitionSlow: '400ms ease-out',
  },

  decorations: {
    cornerFlourishes: true,
    decorativeBorders: true,
    sectionDividers: true,
    motif: 'celtic-knot',
    flourishOpacity: 0.5,
    borderPattern: 'ornate',
    headerStyle: 'underlined',
    panelStyle: 'flat',
  },
};

// ============================================
// Theme Registry
// ============================================

/**
 * All available themes in the registry
 */
export const themeRegistry: MachinaTheme[] = [
  melniboneTheme,
  crimsonKeepTheme,
  feyCourtTheme,
  astralSeaTheme,
  parchmentTheme,
];

/**
 * Get a theme by ID
 */
export function getThemeById(id: string): MachinaTheme | undefined {
  return themeRegistry.find(theme => theme.id === id);
}

/**
 * Get the default theme
 */
export function getDefaultTheme(): MachinaTheme {
  return melniboneTheme;
}

/**
 * Check if a theme is a "light" theme (light background)
 */
export function isLightTheme(theme: MachinaTheme): boolean {
  // Simple heuristic: check if background color is light
  const bg = theme.colors.background;
  const hex = bg.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

/**
 * Theme metadata for UI display
 */
export interface ThemePreview {
  id: string;
  name: string;
  description: string;
  isLight: boolean;
  previewColors: {
    background: string;
    surface: string;
    primary: string;
    accent: string;
    text: string;
  };
}

/**
 * Get preview data for all themes
 */
export function getThemePreviews(): ThemePreview[] {
  return themeRegistry.map(theme => ({
    id: theme.id,
    name: theme.name,
    description: theme.description,
    isLight: isLightTheme(theme),
    previewColors: {
      background: theme.colors.background,
      surface: theme.colors.surface,
      primary: theme.colors.primary,
      accent: theme.colors.accent,
      text: theme.colors.text,
    },
  }));
}
